import { createRoot } from 'react-dom/client';
import { StrictMode } from 'react';
import App from './App.tsx';
import './index.css';
import { AuthProvider } from './hooks/useAuth';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// Enable dark mode by default
document.documentElement.classList.add('dark');

// In dev/editor preview: unregister any existing service workers and clear caches
// VitePWA handles service worker registration automatically in production
if ('serviceWorker' in navigator && (!import.meta.env.PROD || location.hostname.endsWith('.lovable.app'))) {
	navigator.serviceWorker.getRegistrations().then((regs) => {
		regs.forEach((reg) => reg.unregister());
	});
	if ('caches' in window) {
		caches.keys().then((keys) => {
			keys.forEach((key) => caches.delete(key));
		});
	}
}

// Native-shell (Despia/webview) hardening:
// 1) Force all anchors with target="_blank" to carry rel="noopener noreferrer"
// 2) Suppress iOS double-tap zoom on UI chrome only
// Browser Safari must remain user-scalable so a rare viewport zoom can always be escaped.
if (typeof window !== 'undefined') {
	document.addEventListener('click', (e) => {
		const a = (e.target as HTMLElement | null)?.closest?.('a') as HTMLAnchorElement | null;
		if (!a) return;
		if (a.target === '_blank') {
			const rel = (a.getAttribute('rel') || '').toLowerCase();
			if (!rel.includes('noopener')) {
				a.setAttribute('rel', `${rel} noopener noreferrer`.trim());
			}
		}
	}, { capture: true });

	// Prevent iOS double-tap-to-zoom on UI chrome (does not affect inputs)
	let lastTouchEnd = 0;
	document.addEventListener('touchend', (e) => {
		const now = Date.now();
		if (now - lastTouchEnd <= 300) {
			const t = e.target as HTMLElement | null;
			if (t && !t.closest('input, textarea, [contenteditable="true"], .allow-zoom')) {
				e.preventDefault();
			}
		}
		lastTouchEnd = now;
	}, { passive: false });

	// Do not block gesturestart/pinch zoom in mobile browsers; trapping zoom is worse than allowing it.
}

createRoot(document.getElementById('root')!).render(
	<StrictMode>
		<HelmetProvider>
		<BrowserRouter
			future={{
				v7_startTransition: true,
				v7_relativeSplatPath: true,
			}}
		>
				<AuthProvider>
					<App />
				</AuthProvider>
			</BrowserRouter>
		</HelmetProvider>
	</StrictMode>
);
